import { getFacilityOccupancyForecastQueryOptions } from '@/entities/facility/api/get-facility-occupancy-forecast.query';
import { getDynamicChartScale } from '@/shared/lib/charts';
import { MultiLineChart, TooltipElement, Typography } from '@/shared/ui';
import { useQuery } from '@tanstack/react-query';
import type { ChartData, TooltipItem } from 'chart.js';
import dayjs from 'dayjs';
import type { FC } from 'react';

type FacilityOccupancyForecastCardProps = {
  facilityId: string;
};

export const FacilityOccupancyForecastCard: FC<
  FacilityOccupancyForecastCardProps
> = ({ facilityId }) => {
  const now = dayjs();
  const from = now.subtract(12, 'months').startOf('month');
  const to = now.add(3, 'months').endOf('month');

  const { data, isLoading } = useQuery(
    getFacilityOccupancyForecastQueryOptions({
      facilityId,
      from: from.format('YYYY-MM-DD'),
      to: to.format('YYYY-MM-DD'),
    })
  );

  if (!isLoading && !data) {
    return (
      <div className="border-neutral-5 flex h-full w-full flex-col gap-2 border bg-white p-3">
        <Typography variant="h5" weight="medium" className="pb-3">
          Occupancy Actual + Forecasted, %
        </Typography>

        <Typography className="flex flex-1 items-center justify-center">
          No data
        </Typography>
      </div>
    );
  }

  const occupancyForecastLabels =
    data?.actuals && data?.forecast
      ? [
          ...data.actuals.map((x) => dayjs(x.month).format('YYYY MMM')),
          ...data.forecast.map((x) => dayjs(x.month).format('YYYY MMM')),
        ]
      : (data?.actuals?.map((x) => dayjs(x.month).format('YYYY MMM')) ?? []);

  const occupancyForecast: ChartData<'line'> = {
    labels: occupancyForecastLabels,
    datasets: [
      {
        label: 'Actual',
        data: data?.actuals?.map((x) => x.value) ?? [],
        borderColor: '#12408A',
        backgroundColor: 'transparent',
        borderWidth: 2,
        pointBackgroundColor: '#fff',
        pointRadius: 4,
      },
      {
        label: 'Forecast',
        data: [
          ...(data?.actuals?.slice(0, -1).map(() => null) ?? []),
          ...(data?.actuals && data.actuals.length > 0
            ? [data.actuals[data.actuals.length - 1].value]
            : []),
          ...(data?.forecast?.map((x) => x.value) ?? []),
        ],
        borderColor: '#98B2DD',
        backgroundColor: 'transparent',
        borderWidth: 2,
        pointBackgroundColor: '#fff',
        pointRadius: 4,
      },
    ],
  };

  const actuals = data?.actuals.map((a) => a.value) || [];
  const forecasted = data?.forecast.map((f) => f.value) || [];

  const values = [...actuals, ...forecasted];
  const { min, max, stepSize } = getDynamicChartScale(values, 4, 0.1, 100);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        offset: true,
        grid: {
          drawTicks: false,
          offset: true,
        },
        ticks: {
          padding: 20,
          align: 'center' as const,
        },
      },
      y: {
        min,
        max,
        ticks: { stepSize },
        grid: { drawTicks: false },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        filter: (tooltipItem: TooltipItem<'line'>) => {
          const lastActualIndex = (data?.actuals?.length ?? 0) - 1;
          return !(
            tooltipItem.dataIndex === lastActualIndex &&
            tooltipItem.datasetIndex === 1
          );
        },
        callbacks: {
          label: (tooltipItem: TooltipItem<'line'>) =>
            `${tooltipItem.dataset.label}: ${tooltipItem.formattedValue}%`,
        },
      },
      datalabels: {
        display: false,
      },
    },
  };

  return (
    <div className="border-neutral-5 flex h-full w-full flex-col gap-2 border bg-white p-3">
      <div className="flex items-center gap-x-2 pb-3">
        <Typography variant="h5" weight="medium">
          Occupancy Actual + Forecasted, %
        </Typography>
        <TooltipElement content="Displays occupancy for the last 12 completed months and a forecast for the next 3 months. This chart is not affected by the date filter" />
      </div>
      <div className="min-h-0 flex-1">
        <MultiLineChart optionsProps={options} data={occupancyForecast} />
      </div>
    </div>
  );
};
